import { type FormEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import prismLogo from "@/assets/prism-logo.png";
import { ApiError } from "@/lib/api";
import { useLogin } from "@/lib/auth";
import { AuthChrome } from "@/components/auth/AuthChrome";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { OTPInput } from "@/components/ui/otp-input";

interface Props {
  onSuccess: () => void;
  onRequiresSetup: () => void;
}

export function LoginPage({ onSuccess, onRequiresSetup }: Props) {
  const { t } = useTranslation();
  const login = useLogin();
  const formRef = useRef<HTMLFormElement>(null);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [need2fa, setNeed2fa] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Core mutation — called from both form-submit and OTP onComplete.
   * Accepts an explicit `code` arg so it can be called before React
   * re-renders `totpCode` state.
   */
  const doLogin = async (code?: string) => {
    setError(null);
    try {
      const result = await login.mutateAsync({
        username,
        password,
        totp_code: (code ?? totpCode) || undefined,
      });
      if (result.requires_2fa_setup) {
        onRequiresSetup();
      } else if (result.requires_2fa) {
        setNeed2fa(true);
      } else {
        onSuccess();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail);
      } else {
        setError(t("login.error_unknown"));
      }
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void doLogin();
  };

  return (
    <AuthChrome>
      <Card className="w-full max-w-sm shadow-md">
        <CardHeader className="pb-2 text-center">
          <img
            src={prismLogo}
            alt="Prism"
            className="mx-auto mb-2 h-10 w-auto select-none"
            draggable={false}
          />
          <h1 className="text-3xl font-bold tracking-tight text-fg-primary">
            {t("login.title")}
          </h1>
          <p className="mt-1 text-sm text-fg-muted">{t("login.subtitle")}</p>
        </CardHeader>

        <CardContent className="pt-4">
          {/* Step indicator — shows steps 1→2 once 2FA is needed */}
          {need2fa && (
            <div className="mb-4 flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent/15 text-xs font-semibold text-accent">
                  1
                </span>
                <span className="text-xs text-fg-muted">{t("auth.step_credentials")}</span>
              </div>
              <div className="h-px flex-1 bg-border" />
              <div className="flex items-center gap-1.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent text-xs font-semibold text-fg-on-accent">
                  2
                </span>
                <span className="text-xs font-medium text-fg-primary">{t("auth.step_2fa")}</span>
              </div>
            </div>
          )}

          <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
            {/* Credentials fields — locked after 2FA is required */}
            <div className="space-y-2">
              <Label htmlFor="username">{t("login.username")}</Label>
              <Input
                id="username"
                autoComplete="username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={need2fa || login.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t("login.password")}</Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={need2fa || login.isPending}
              />
            </div>

            {/* 2FA TOTP step */}
            {need2fa && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 rounded-lg bg-accent/10 px-3 py-2">
                  <ShieldCheck
                    size={16}
                    strokeWidth={2}
                    className="shrink-0 text-accent"
                    aria-hidden="true"
                  />
                  <p className="text-xs text-fg-secondary">{t("login.totp_hint")}</p>
                </div>

                <div className="space-y-2">
                  <Label>{t("login.totp")}</Label>
                  <OTPInput
                    value={totpCode}
                    onChange={setTotpCode}
                    onComplete={(code) => void doLogin(code)}
                    disabled={login.isPending}
                    autoFocus
                    ariaLabel={t("login.totp")}
                  />
                </div>
              </div>
            )}

            {error && (
              <Alert tone="danger" icon>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              loading={login.isPending}
              size="lg"
            >
              {t("login.submit")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthChrome>
  );
}
